import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRooms } from '../../hooks/useRooms';
import { useRoomLabel } from '../../hooks/useRoomLabel';

const STORAGE_KEY = 'agendaRoomId';

// Filtro de sala/consultório da agenda. O filtro é aplicado no cliente porque
// o AppointmentToCalendarDTO já traz o roomId — mesma estratégia do filtro de
// profissional (useCalendarAppointments, pelo dentistId), então os dois se
// combinam sobre a mesma lista, sem ida ao backend a cada troca.
export function useRoomFilter() {
  const { rooms } = useRooms();
  const roomLabel = useRoomLabel();

  const [selectedRoomId, setSelectedRoomId] = useState(
    () => sessionStorage.getItem(STORAGE_KEY) || '',
  );

  // Preserva o filtro ao navegar para /appointment e voltar, como já é feito
  // com a visão e a data da agenda.
  useEffect(() => {
    if (selectedRoomId) {
      sessionStorage.setItem(STORAGE_KEY, selectedRoomId);
      return;
    }
    sessionStorage.removeItem(STORAGE_KEY);
  }, [selectedRoomId]);

  // Uma sala excluída do cadastro não pode continuar filtrando a agenda.
  useEffect(() => {
    if (!selectedRoomId || rooms.length === 0) {
      return;
    }
    const stillExists = rooms.some((room) => String(room.id) === String(selectedRoomId));
    if (!stillExists) {
      setSelectedRoomId('');
    }
  }, [rooms, selectedRoomId]);

  const filterAppointmentsByRoom = useCallback(
    (appointments) =>
      selectedRoomId
        ? appointments.filter(
            (appointment) => String(appointment.roomId) === String(selectedRoomId),
          )
        : appointments,
    [selectedRoomId],
  );

  // Bloqueios sem sala valem para a clínica inteira (ou para um profissional em
  // todas as salas) e continuam visíveis com o filtro ativo — esconder faria o
  // usuário agendar em cima de um feriado.
  const filterBlocksByRoom = useCallback(
    (blocks) =>
      selectedRoomId
        ? blocks.filter((block) => !block.roomId || String(block.roomId) === String(selectedRoomId))
        : blocks,
    [selectedRoomId],
  );

  const selectedRoom = useMemo(
    () => rooms.find((room) => String(room.id) === String(selectedRoomId)) ?? null,
    [rooms, selectedRoomId],
  );

  return {
    rooms,
    roomLabel,
    selectedRoomId,
    setSelectedRoomId,
    selectedRoom,
    // Sem mais de uma sala cadastrada o filtro não tem função — mesma regra do
    // combo de sala no cadastro de consulta.
    isRoomFilterVisible: rooms.length > 1,
    clearRoomFilter: () => setSelectedRoomId(''),
    filterAppointmentsByRoom,
    filterBlocksByRoom,
  };
}
